import React, { useMemo, useState, useEffect, useCallback } from 'react'
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  Tooltip,
  IconButton,
  Box,
  lighten,
  useTheme
} from '@mui/material'
import { Link } from 'react-router-dom'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EmailIcon from '@mui/icons-material/Email'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import MyTableExpandableRow from './MyTableExpandableRow'
import MyTableCellHeader from './MyTableCellHeader'
import MyTableCell from './MyTableCell'
import MyTablePaginationActions from './MyTablePaginationActions'
import ConfirmationButton from '../../misc/ConfirmationButton'
import EmailDialog from '../../dialogs/EmailDialog'
import { useTableSort } from '../../hooks/useTableSort'
import { useAuth } from '../../../context/AuthContext'

dayjs.extend(utc)
dayjs.extend(timezone)

function MyTable({ mode = 'view', data, deleteData, tableOptions, columns, setIsUpdated }) {
  const { user } = useAuth()
  const userId = user?.id || 'anon'
  const headerStyle = useMemo(() => ({ height: 30, padding: 10 }), [])
  const cellStyle = useMemo(() => ({ height: 30, padding: 10 }), [])
  const paperStyle = useMemo(() => tableOptions?.paperStyle || { width: '90%', marginLeft: '5%' }, [tableOptions])
  const tableStyle = useMemo(
    () =>
      tableOptions?.tableStyle || {
        maxHeight: '100%',
        height: '100%',
        overflowY: 'auto'
      },
    [tableOptions]
  )
  const actionColPos = tableOptions?.actionColumnPosition === 'last' ? 'last' : 'first'
  const object = tableOptions?.objectName || tableOptions?.objectType || 'items'
  const storageKey = `myTable_${userId}_${object}`

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [emailOpen, setEmailOpen] = useState(false)
  const [selectedEmail, setSelectedEmail] = useState(null)
  const [expandedRowId, setExpandedRowId] = useState(null)
  const theme = useTheme()
  const WHITE_COLOR = '#FFFFFF'
  const ODD_ROW_COLOR = lighten(theme.palette.primary.main, 0.85)
  const columnMetaMap = useMemo(() => {
    const map = {}
    columns.forEach((c) => {
      if (c.name) map[c.name] = c
      if (c.display) map[c.display] = c
    })
    return map
  }, [columns])

  const visibleColumns = useMemo(() => columns.filter((c) => c.isVisible === undefined || c.isVisible), [columns])

  const getByPath = useCallback((name, row) => name.split('.').reduce((v, p) => v?.[p], row), [])

  const getField = useCallback(
    (name, row) => {
      const value = getByPath(name, row)
      const meta = columnMetaMap[name]
      if (!meta) return value

      if (meta.type === 'currency' && value != null) {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value)
      }

      if (meta.type === 'float' && value != null) {
        return new Intl.NumberFormat('fr-FR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(value)
      }

      if (meta.type === 'date' && meta.format && value != null) {
        return dayjs.utc(value).local().format(meta.format)
      }

      if (meta.mapping) {
        return meta.mapping(value, row)
      }

      return value
    },
    [columnMetaMap, getByPath]
  )

  useEffect(() => {
    const savedPage = parseInt(localStorage.getItem(`${storageKey}_page`) || '0', 10)
    const savedRows = parseInt(localStorage.getItem(`${storageKey}_rows`) || '5', 10)
    const savedExpanded = localStorage.getItem(`${storageKey}_expandedRow`)
    setPage(Number.isNaN(savedPage) ? 0 : savedPage)
    setRowsPerPage(Number.isNaN(savedRows) ? 5 : savedRows)
    setExpandedRowId(savedExpanded ? Number(savedExpanded) : null)
  }, [storageKey])

  useEffect(() => localStorage.setItem(`${storageKey}_page`, String(page)), [page, storageKey])
  useEffect(() => localStorage.setItem(`${storageKey}_rows`, String(rowsPerPage)), [rowsPerPage, storageKey])
  useEffect(
    () => localStorage.setItem(`${storageKey}_expandedRow`, expandedRowId == null ? '' : String(expandedRowId)),
    [expandedRowId, storageKey]
  )

  const rows = useMemo(() => (Array.isArray(data?.[object]) ? data[object] : []), [data, object])
  const { sortedData, sortedColumn, sortedColumnMode, handleSort } = useTableSort(rows, visibleColumns)

  useEffect(() => {
    localStorage.setItem(`${storageKey}_sortedColumn`, sortedColumn || '')
  }, [sortedColumn, storageKey])
  useEffect(() => {
    localStorage.setItem(`${storageKey}_sortedMode`, sortedColumnMode || '')
  }, [sortedColumnMode, storageKey])

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage
    return sortedData.slice(start, start + rowsPerPage)
  }, [sortedData, page, rowsPerPage])

  const handleChangePage = (_e, newPage) => setPage(newPage)
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(+e.target.value)
    setPage(0)
  }

  const handleEmailOpen = (id) => {
    setSelectedEmail(id)
    setEmailOpen(true)
  }

  const ActionCell = useCallback(
    ({ row }) => {
      if (mode !== 'view' || !tableOptions?.actions) return null
      return (
        <TableCell style={cellStyle} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tableOptions?.accessRight?.is_view && tableOptions.actions.view && (
            <Tooltip title='Visualiser'>
              <IconButton component={Link} to={`/${tableOptions.objectType}/view`} state={{ id: row.id }}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}

          {tableOptions?.accessRight?.is_edit && tableOptions.actions.edit && (
            <Tooltip title='Modifier'>
              <IconButton
                component={Link}
                to={`/${tableOptions.objectType}/edit`}
                state={{ id: row.id }}
                disabled={tableOptions.actions.editDisabled?.(row)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}

          {tableOptions?.accessRight?.is_view && tableOptions.actions.go && (
            <Tooltip title='Aller'>
              <IconButton
                aria-label='go'
                component={Link}
                state={{ id: row.id, history: `/${tableOptions.objectType}` }}
                to={`/${tableOptions.objectType}/view`}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}

          {tableOptions.actions.email && (
            <Tooltip title='Email'>
              <IconButton onClick={() => handleEmailOpen(row.id)}>
                <EmailIcon />
              </IconButton>
            </Tooltip>
          )}

          {tableOptions?.accessRight?.is_delete && tableOptions.actions.delete && (
            <ConfirmationButton
              type='Icon'
              tooltip='Supprimer'
              message={
                tableOptions.actions.delete.field
                  ? `${tableOptions.actions.delete.message} ${row[tableOptions.actions.delete.field]}?`
                  : tableOptions.actions.delete.message
              }
              color='primary'
              onClickOk={() => deleteData(row.id)}
            >
              <DeleteIcon />
            </ConfirmationButton>
          )}

          {tableOptions.actions.customActions?.map((action) => (
            <Tooltip key={`custom-${row.id}-${action.title}`} title={action.title}>
              <IconButton onClick={() => action.onClick(row.id)}>{action.icon()}</IconButton>
            </Tooltip>
          ))}
        </TableCell>
      )
    },
    [mode, tableOptions, cellStyle, deleteData]
  )

  const rowsToRender = useMemo(() => {
    return paginatedData.map((row, rowIndex) => {
      const isStripe = rowIndex % 2 !== 0
      const rowBg = isStripe ? ODD_ROW_COLOR : WHITE_COLOR

      const rowKey = row?.id != null ? `row-${row.id}` : `page${page}-row${rowIndex}`
      const cells = visibleColumns.map((col) => (
        <TableCell style={{ ...cellStyle, verticalAlign: 'top' }} key={`${rowKey}-cell-${col.name}`}>
          <MyTableCell
            value={col.display ? getField(col.display, row) : getField(col.name, row)}
            component={col.component}
          />
        </TableCell>
      ))
      const actionCell = tableOptions?.actions ? <ActionCell row={row} /> : null
      const rowStyle = { backgroundColor: rowBg }
      return tableOptions?.isExpand ? (
        <MyTableExpandableRow
          key={rowKey}
          sx={rowStyle}
          isExpanded={expandedRowId === row.id}
          onToggle={() => setExpandedRowId((prev) => (prev === row.id ? null : row.id))}
          expandComponent={tableOptions.expand ? tableOptions.expand(row) : null}
          totalColumns={tableOptions.columns.length}
        >
          {actionColPos === 'first' && actionCell}
          {cells}
          {actionColPos !== 'first' && actionCell}
        </MyTableExpandableRow>
      ) : (
        <TableRow key={rowKey} sx={rowStyle}>
          {actionColPos === 'first' && actionCell}
          {cells}
          {actionColPos !== 'first' && actionCell}
        </TableRow>
      )
    })
  }, [paginatedData, page, visibleColumns, tableOptions, expandedRowId, actionColPos, getField, cellStyle])

  const headerCells = useMemo(() => {
    return visibleColumns.map((col) => (
      <MyTableCellHeader
        key={`${col.name}-header`}
        header={col}
        sort={handleSort}
        sortedColumn={sortedColumn}
        sortedColumnMode={sortedColumnMode}
      />
    ))
  }, [visibleColumns, handleSort, sortedColumn, sortedColumnMode])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sortedData.length / rowsPerPage) - 1)
    if (page > maxPage) {
      setPage(maxPage)
    }
  }, [sortedData.length, rowsPerPage, page])

  return (
    <Paper
      variant='outlined'
      style={{
        ...paperStyle,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}
    >
      <TableContainer
        style={{
          ...tableStyle,
          flex: 1,
          overflowY: 'auto',
          minHeight: 0
        }}
      >
        <Table stickyHeader sx={{ tableLayout: 'fixed', minWidth: '100%' }}>
          <TableHead>
            <TableRow style={headerStyle}>
              {tableOptions?.isExpand && (
                <MyTableCellHeader
                  header={{
                    label: '',
                    isSorting: false,
                    isExpand: true
                  }}
                />
              )}
              {tableOptions?.actions && actionColPos === 'first' && (
                <MyTableCellHeader header={{ label: 'Actions', isSorting: false }} />
              )}
              {headerCells}
              {tableOptions?.actions && actionColPos !== 'first' && (
                <MyTableCellHeader header={{ label: 'Actions', isSorting: false }} />
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {rowsToRender.length > 0 ? (
              rowsToRender
            ) : (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length + (tableOptions?.actions ? 1 : 0) + (tableOptions?.isExpand ? 1 : 0)}
                  align='center'
                  style={{ height: 150 }}
                >
                  Aucune donnée disponible
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          {tableOptions?.showTotal?.display && (
            <TableFooter>
              <TableRow>
                <TableCell align='right'>{tableOptions.showTotal.label}</TableCell>
                <TableCell align='left'>
                  {tableOptions.showTotal.type === 'currency'
                    ? new Intl.NumberFormat('fr-FR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      }).format(data?.[tableOptions.showTotal.field] ?? 0) + ' €'
                    : data?.[tableOptions.showTotal.field]}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component='div'
        count={sortedData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        labelRowsPerPage='Lignes par page :'
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
        ActionsComponent={MyTablePaginationActions}
      />
      <EmailDialog open={emailOpen} setOpen={setEmailOpen} setIsUpdated={setIsUpdated} id={selectedEmail} />
    </Paper>
  )
}

export default MyTable
